import { WarningAmberRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { ThemePalette } from 'mui.theme';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useAppSelector } from 'store/hook';
import { isAllTimeLineDataEmpty } from 'utils/driverBehavior';
import '../styles/timeline.css';
import useAppTheme from 'hooks/useAppTheme';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useCustomerInfo from 'hooks/useCustomerInfo';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useStartEndDates from 'views/EngineHours/hooks/useStartEndDates';
import { DumpingTimeLineChartTooltip } from './DumpingTimeLineChartTooltip';
import {
  locationColors,
  shiftColorPalette,
} from 'views/FleetPlanner/utils/constants';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

const chartHeight = 200;

export const DumpingTimeLineChart = () => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const selectedPlanData = useAppSelector((state) =>
    state.fleetPlanningReducer.selectedPlan === 'user'
      ? state.fleetPlanningReducer.userPlan
      : state.fleetPlanningReducer.optimizedPlan
  );
  const planTimeWindow = useAppSelector(
    (state) => state.fleetPlanningReducer.planTimeWindow
  );

  const { shiftsWithTime } = useStartEndDates({
    showShiftWise: true,
    startDate: new Date(),
    endDate: new Date(),
  });

  const { startDateWithTime, endDateWithTime } = useMemo(() => {
    const planStart = planTimeWindow
      ? dayjs.utc(planTimeWindow.startTime).valueOf()
      : null;
    const planEnd = planTimeWindow
      ? dayjs.utc(planTimeWindow.endTime).valueOf()
      : null;

    if (!selectedPlanData.planData || selectedPlanData.planData.length === 0) {
      return {
        startDateWithTime: planStart ?? dayjs.utc().valueOf(),
        endDateWithTime: planEnd ?? dayjs.utc().valueOf(),
      };
    }

    const allTimestamps = selectedPlanData.planData.flatMap((d) => [
      dayjs.utc(d.start_ts).valueOf(),
      dayjs.utc(d.end_ts).valueOf(),
    ]);
    allTimestamps.sort((a, b) => a - b);
    const dataStart = allTimestamps[0];
    const dataEnd = allTimestamps[allTimestamps.length - 1];

    return {
      startDateWithTime: Math.min(planStart ?? dataStart, dataStart),
      endDateWithTime: Math.max(planEnd ?? dataEnd, dataEnd),
    };
  }, [selectedPlanData.planData, planTimeWindow]);

  const chartOptions = useMemo<ApexOptions>(() => {
    let annotations: ApexAnnotations | undefined;
    let xAxisAnnotations: any[] = [];

    if (shiftsWithTime && shiftsWithTime?.length > 0) {
      xAxisAnnotations = shiftsWithTime.map((shift, index) => ({
        x: new Date(shift.startDate).getTime(),
        x2: new Date(shift.endDate).getTime(),
        borderColor: shiftColorPalette[index % shiftColorPalette.length],
        fillColor: shiftColorPalette[index % shiftColorPalette.length],
        opacity: 0.1,
        label: {
          style: {
            color: '#000',
          },
        },
      }));
    }

    if (xAxisAnnotations.length > 0) {
      annotations = {
        xaxis: xAxisAnnotations,
      };
    }

    return {
      chart: {
        height: 950,
        toolbar: {
          show: false,
          tools: {
            download: false,
          },
        },
        zoom: {
          enabled: false,
        },
        animations: {
          dynamicAnimation: {
            enabled: false,
          },
        },
        selection: {
          enabled: false,
        },
        brush: {
          enabled: false,
        },
      },
      tooltip: {
        custom: (props) =>
          DumpingTimeLineChartTooltip({
            ...props,
            isMarginTop: true,
            preferredTimeDisplayMode: 'configured',
            timezone: customerTz,
          }),
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%',
          borderRadius: 2,
        },
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.1,
          },
        },
        active: {
          filter: {
            type: 'darken',
            value: 0.1,
          },
        },
      },
      colors: shiftColorPalette,
      xaxis: {
        type: 'datetime',
        position: 'top',
        min: startDateWithTime,
        max: endDateWithTime,
        labels: {
          datetimeUTC: true,
          style: {
            colors: palette.textPrimary,
          },
          formatter: (value: any) => {
            return dayjs.utc(Number(value)).tz(customerTz).format('HH:mm');
          },
        },
      },
      yaxis: {
        show: false,
      },
      grid: {
        column: {
          colors: ['#152A4B', '#162C4E'],
        },
      },
      stroke: {
        width: 1,
      },
      fill: {
        type: 'solid',
        opacity: 0.6,
      },
      annotations,
    };
  }, [
    shiftsWithTime,
    enableDarkTheme,
    customerTz,
    startDateWithTime,
    endDateWithTime,
  ]);

  const memoizedChartOptions = useMemo(() => {
    return {
      ...chartOptions,
      chart: {
        ...chartOptions.chart,
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      xaxis: {
        ...chartOptions.xaxis,
      },
    };
  }, [chartOptions, shiftsWithTime, enableDarkTheme]);

  const dumpingSeries = useMemo(() => {
    if (!Array.isArray(selectedPlanData.planData)) {
      console.warn('planData missing or not array');
      return [];
    }

    const lastLoadByTruck: Record<
      string,
      { location: string; materialDumped: number } | null
    > = {};

    const dumpingData: any[] = [];

    selectedPlanData.planData.forEach((item) => {
      if (item.action === 'loading') {
        // store the load origin for this truck
        // lastLoadByTruck[item.device] = item.start_location;
        lastLoadByTruck[item.device] = {
          location: item.start_location,
          materialDumped: item.material_dumped,
        };
      }

      if (item.action === 'dumping') {
        const start = dayjs.utc(item.start_ts);
        const end = dayjs.utc(item.end_ts);

        if (start.isValid() && end.isValid() && start.isBefore(end)) {
          const originLocation =
            lastLoadByTruck[item.device]?.location?.toLowerCase() ?? '';
          const materialPickedFromOriginLocation =
            lastLoadByTruck[item.device]?.materialDumped ?? 0;
          const { fill, stroke } =
            locationColors[originLocation] ?? locationColors.other;

          dumpingData.push({
            x: 'Dumping',
            y: [start.valueOf(), end.valueOf()],
            fillColor: fill,
            strokeColor: stroke,
            extraDetails: {
              bumperNumber: item.bumper_number,
              startLocation: originLocation,
              endLocation: originLocation,
              start: start.valueOf(),
              end: end.valueOf(),
              material_dumped: materialPickedFromOriginLocation,
            },
          });
        }
      }
    });

    return dumpingData.length ? [{ name: 'Dumping', data: dumpingData }] : [];
  }, [selectedPlanData.planData]);

  return (
    <div
      style={{
        height: '300px',
        position: 'relative',
        borderRadius: 2,
        backgroundColor: enableDarkTheme ? 'rgba(38, 79, 225, 0.1)' : '#fff',
        width: '100%',
        border: enableDarkTheme ? '1px solid #334155' : 'none',
      }}
    >
      <div
        style={{
          padding: '0px 16px',
          color: palette.textPrimary,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {isAllTimeLineDataEmpty(dumpingSeries) ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <WarningAmberRounded />
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '14px',
              }}
            >
              {t('no_timeline_data_available')}
            </Typography>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 16,
                marginBottom: 8,
                padding: '0px',
                height: '55px',
              }}
            >
              <div
                key="Loading"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  gap: '8px',
                }}
              >
                <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l0.fill,
                      border: `1px solid ${locationColors.l0.stroke}`,
                      marginRight: 2,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l0_2')}
                  </span>
                </Box>

                <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l1.fill,
                      border: `1px solid ${locationColors.l1.stroke}`,
                      marginRight: 2,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l1_2')}
                  </span>
                </Box>
              </div>
            </div>
            <div
              style={{
                flexGrow: 1,
                overflowX: 'auto',
              }}
            >
              <div style={{ width: '5000px' }}>
                <ReactApexChart
                  options={memoizedChartOptions}
                  series={dumpingSeries}
                  key="dumping-timeline"
                  type="rangeBar"
                  height={chartHeight}
                  width={'100%'}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
